import { useState, useEffect } from 'react';
import { hashtagsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Hash, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PALETTE = ['#6d28d9', '#2563eb', '#0891b2', '#059669', '#ca8a04', '#dc2626', '#db2777', '#475569'];

export function HashtagManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [label, setLabel] = useState('');
    const [color, setColor] = useState(PALETTE[0]);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try { const r = await hashtagsAPI.list(); setItems(r.data || []); }
        catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!label.trim()) { toast.error('Nom du hashtag requis'); return; }
        try {
            setSaving(true);
            await hashtagsAPI.create({ label: label.trim(), color });
            toast.success('Hashtag créé'); setLabel(''); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm('Supprimer ce hashtag de tous les dossiers ?')) return;
        try { await hashtagsAPI.remove(id); toast.success('Supprimé'); load(); }
        catch { toast.error('Erreur'); }
    };

    const changeColor = async (id, newColor) => {
        try { await hashtagsAPI.update(id, { color: newColor }); load(); }
        catch { toast.error('Erreur'); }
    };

    return (
        <Card data-testid="hashtag-manager">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Hash className="w-5 h-5" /> Hashtags des dossiers
                </CardTitle>
                <p className="text-sm text-slate-500">
                    Créez les # disponibles (avec couleur). Tous les utilisateurs pourront ensuite taguer les dossiers,
                    par module (Courtage / Travaux d’optimisation).
                </p>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* Création */}
                <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-slate-50 p-3">
                    <div className="flex-1 min-w-[160px]">
                        <label className="text-xs text-slate-500">Nom du hashtag</label>
                        <Input value={label} onChange={(e) => setLabel(e.target.value)}
                            placeholder="ex: Prioritaire" data-testid="hashtag-new-label"
                            onKeyDown={(e) => e.key === 'Enter' && create()} />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Couleur</label>
                        <div className="flex items-center gap-1">
                            {PALETTE.map(c => (
                                <button key={c} type="button" onClick={() => setColor(c)}
                                    className={`h-6 w-6 rounded-full border-2 ${color === c ? 'border-slate-800' : 'border-transparent'}`}
                                    style={{ backgroundColor: c }} data-testid={`hashtag-color-${c}`} />
                            ))}
                            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                                className="h-6 w-8 cursor-pointer rounded border-0 bg-transparent p-0" title="Couleur personnalisée" />
                        </div>
                    </div>
                    <Button onClick={create} disabled={saving} data-testid="hashtag-create-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Créer</>}
                    </Button>
                </div>

                {/* Liste */}
                {loading ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : items.length === 0 ? (
                    <p className="py-4 text-center text-sm text-slate-400">Aucun hashtag pour l'instant.</p>
                ) : (
                    <div className="flex flex-wrap gap-2" data-testid="hashtag-list">
                        {items.map(h => (
                            <div key={h.id} className="flex items-center gap-2 rounded-full border bg-white py-1 pl-1 pr-2 shadow-sm">
                                <Badge style={{ backgroundColor: h.color, color: '#fff' }} className="gap-0.5 border-0">
                                    <Hash className="w-3 h-3" />{h.label}
                                </Badge>
                                <input type="color" value={h.color} onChange={(e) => changeColor(h.id, e.target.value)}
                                    className="h-5 w-5 cursor-pointer rounded border-0 bg-transparent p-0" title="Changer la couleur" />
                                <button onClick={() => remove(h.id)} className="text-slate-400 hover:text-red-500"
                                    data-testid={`hashtag-delete-${h.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
